// The signed-in agent's open drafts, shared by the tab row (DraftTabs.jsx) and the create
// screen (pages/tickets/New.jsx), which reports each draft's save state here so the row
// can mark it (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md, "The tab
// row").
//
// The list is GET /api/ticket-drafts (docs/API.md §12.2): summaries, never contents. It
// is read on mount, after a reported save lands or a draft closes, when the browser comes
// back online, and when the browser tab regains focus. On focus who is signed in is
// checked FIRST (SessionProvider's `checkIdentity`): a different person reloads the page,
// and only a confirmed same person has the list refreshed.
//
// Retrying a failed SAVE is not done here; the save controller lives in the create screen.
//
// It also brings back drafts whose FIRST save never landed (a 401 redirect, a crash, a
// reboot): their text is only in this browser's write-ahead buffer, so the list cannot
// show them. `recovered` is the signed-in user's buffered drafts at base revision 0
// (tab-row.js `recoverableDraftIds`), read again whenever the list is read or a draft's
// reported state changes, and offered only once the list has loaded, so a listed draft
// never shows twice. Opening one replays its buffer (pages/tickets/New.jsx). Discarding or
// filing it removes the buffer entry, so its tab goes.
//
// It also holds "Ticket #N created" notices for a draft filed while the agent was looking
// at another page: the create form that filed it has unmounted by then, so the notice
// lives here and the tab row shows it (spec, "Filing from a tab").

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { api } from '../../api/client.js';
import { useCheckIdentity, useSession } from '../../session/SessionProvider.jsx';
import { newDraftId } from './draft-model.js';
import { draftHref } from './draft-address.js';
import { browserStorage, createBuffer } from './draft-buffer.js';
import { recoverableDraftIds } from './tab-row.js';

const DraftsContext = createContext(null);
const NONE = [];

const sameIds = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);

// A report into one of these means the server's list has moved: a draft was saved (its
// label may have changed, or it now exists), closed, changed elsewhere, or taken over.
const LIST_MOVED = new Set(['filed', 'discarded', 'stale', 'taken']);

export function DraftsProvider({ children }) {
  const navigate = useNavigate();
  const checkIdentity = useCheckIdentity();
  const userId = useSession()?.id;
  const [buffer] = useState(() => createBuffer(browserStorage()));
  const [drafts, setDrafts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // Ids of this user's never-saved drafts in the buffer. Ids only: no draft text is held here.
  const [recovered, setRecovered] = useState(NONE);
  const [statuses, setStatuses] = useState(() => new Map());
  // Ticket numbers filed from a draft the agent was no longer looking at, oldest first.
  const [filed, setFiled] = useState([]);
  // The latest snapshot per draft, kept outside state so a report that does not change
  // the state (a new revision, the same `idle`) re-renders nothing.
  const reported = useRef(new Map());
  const mounted = useRef(false);
  const inFlight = useRef(null);
  const again = useRef(false);

  // The buffer is read under the SESSION user's id, so another agent's entries on a shared
  // desk never reach this row. The same array is kept while nothing changed.
  const readRecovered = useCallback(() => {
    const next = userId ? recoverableDraftIds(buffer.pending(userId)) : NONE;
    setRecovered((prev) => (sameIds(prev, next) ? prev : next));
  }, [buffer, userId]);

  // One read at a time. A refresh asked for while one is running is not dropped: the
  // running one reads once more when it finishes, so the row ends on the newest list.
  // A failed read keeps the list it had. The buffer is read after every attempt.
  const refresh = useCallback(() => {
    if (inFlight.current) {
      again.current = true;
      return inFlight.current;
    }
    const run = (async () => {
      do {
        again.current = false;
        try {
          const response = await api.get('/ticket-drafts');
          if (mounted.current) {
            setDrafts(Array.isArray(response?.data) ? response.data : []);
            setLoaded(true);
          }
        } catch {
          // Offline, or the server is down: the row keeps what it last showed, and the
          // `online` and focus listeners below ask again.
        }
        if (mounted.current) readRecovered();
      } while (again.current && mounted.current);
    })().finally(() => { inFlight.current = null; });
    inFlight.current = run;
    return run;
  }, [readRecovered]);

  /**
   * The create screen's report of a draft's save state: a draft-sync.js snapshot
   * (`{ state, revision, ...extras }`), or null to forget the draft. Call it from the
   * controller's `onState`. Re-renders the row only when the state changes, and refreshes
   * the list when a save lands (saving → idle) or the draft is closed, refused as stale, or
   * taken over.
   */
  const reportStatus = useCallback((draftId, snapshot) => {
    if (!draftId) return;
    const before = reported.current.get(draftId)?.state;
    if (snapshot) reported.current.set(draftId, snapshot);
    else reported.current.delete(draftId);
    const after = snapshot?.state;
    if (before === after) return;
    setStatuses(new Map(reported.current));
    // A replayed, filed or discarded draft may just have left the buffer.
    readRecovered();
    if ((before === 'saving' && after === 'idle') || LIST_MOVED.has(after)) void refresh();
  }, [refresh, readRecovered]);

  /**
   * Opens a new draft: a freshly minted GUID, with an optional one-time prefill
   * (`{ parent?, customer? }`, as draft-address.js reads it from a query string) in
   * router state. Returns the new id. Links use `/tickets/new` instead, which does the
   * same from its query string.
   */
  const startNew = useCallback((prefill = null) => {
    const id = newDraftId();
    navigate(draftHref(id), { state: { prefill } });
    return id;
  }, [navigate]);

  /** Shows "Ticket #N created" in the tab row until dismissed. */
  const announceFiled = useCallback((publicRef) => {
    const ref = String(publicRef);
    setFiled((prev) => (prev.includes(ref) ? prev : [...prev, ref]));
  }, []);

  const dismissFiled = useCallback((publicRef) => {
    setFiled((prev) => prev.filter((ref) => ref !== String(publicRef)));
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh();

    function onFocus() {
      void checkIdentity().then((sameUser) => {
        if (sameUser) void refresh();
      });
    }
    function onOnline() {
      void refresh();
    }
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      mounted.current = false;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [refresh, checkIdentity]);

  // Until the list has loaded, a recovered id might be one it lists: none is offered yet.
  const offered = loaded ? recovered : NONE;
  const value = useMemo(
    () => ({
      drafts, loaded, statuses, recovered: offered, refresh, reportStatus, startNew, filed, announceFiled, dismissFiled,
    }),
    [drafts, loaded, statuses, offered, refresh, reportStatus, startNew, filed, announceFiled, dismissFiled],
  );

  return <DraftsContext.Provider value={value}>{children}</DraftsContext.Provider>;
}

/**
 * `{ drafts, loaded, statuses, recovered, refresh, reportStatus, startNew, filed,
 * announceFiled, dismissFiled }`. Never null inside the provider, which the platform shell
 * puts around Support's header, its tab row and every Support page (Support's entry hands it
 * over as its `Provider`: support-module/entry.jsx, web/shell/PlatformLayout.jsx).
 */
export function useDrafts() {
  const drafts = useContext(DraftsContext);
  if (!drafts) throw new Error('useDrafts must be used inside <DraftsProvider>');
  return drafts;
}
